import { memo, useCallback, useState } from 'react';

import { useAuth } from '../lib/auth';
import { setType } from '../lib/delivery';
import { DeliveryItem, DeliveryStatus, DeliveryType } from '../lib/types';
import { MainButton } from './buttons';
import Fulfillment from './Fulfillment';
import { Colors, Detail, Divider } from './general';
import { Popup, Sidebar } from './modal';
import Pricing from './pricing';

interface CartProps {
  onCheckout: () => void;
  onDismiss: () => void;
  showing: boolean;
  type: 'popup' | 'sidebar';
}

const getName = (relationshipId: string): string => {
  switch (relationshipId) {
    case 'divorce':
      return `You're divorced`;
    case 'lived-together':
      return `You lived together`;
    case 'serious':
      return `It was serious`;
    case 'casual':
      return `It was casual`;
    case 'slept-ex':
      return `You slept with your ex`;
    case 'hookup':
      return `You hooked up`;
    case 'one-night-stand':
      return `You had a one night stand`;
    case 'friendzone':
      return `You got friendzoned`;
    default:
      return `Your ex`;
  }
};

const getRecap = (relationshipId: string, type: DeliveryType): string[] => {
  switch (relationshipId) {
    case 'divorce':
      return type === DeliveryType.request
        ? [`...and you want your stuff back.`, ``]
        : [`...and you're sending their stuff back.`, ``];
    case 'lived-together':
      return type === DeliveryType.request
        ? [`...and it didn't work out.`, `Now, you want your stuff back.`]
        : [`...and it didn't work out.`, `You're sending their stuff back.`];
    case 'serious':
      return type === DeliveryType.request
        ? [`...until it wasn't.`, `You want your stuff back.`]
        : [`...until it wasn't.`, `You're sending their stuff back.`];
    case 'casual':
      return type === DeliveryType.request
        ? [`...too casual.`, `You just want your stuff back.`]
        : [`...too casual.`, `You're sending their stuff back`];
    case 'slept-ex':
      return type === DeliveryType.request
        ? [`...again.`, `You just want your stuff back.`]
        : [`...again.`, `You're sending their stuff back`];
    case 'hookup':
      return type === DeliveryType.request
        ? [`...and now it's over.`, `You want your stuff back.`]
        : [`...and now it's over.`, `You're sending their stuff back.`];
    case 'one-night-stand':
      return type === DeliveryType.request
        ? [`...that's iconic.`, `You want your stuff back.`]
        : [`...that's iconic.`, `You're sending their stuff back.`];
    case 'friendzone':
      return type === DeliveryType.request
        ? [`...that sucks.`, `You want your stuff back.`]
        : [`...that sucks.`, `You're sending their stuff back.`];
    default:
      return type === DeliveryType.request
        ? [`You left your stuff at someone's house.`, `You want it back.`]
        : [`Someone left their stuff at your house.`, `You're sending it back.`];
  }
};

const Cart: React.FC<CartProps> = ({ onCheckout, onDismiss, showing, type }) => {
  const { delivery, deliveryType, updateType, updateDelivery } = useAuth();
  const [loading, setLoading] = useState(false);

  const onNext = useCallback(() => {
    setLoading(true);
    onCheckout();
  }, [onCheckout]);

  const onRemove = useCallback(
    (item: DeliveryItem) => {
      if (!delivery) {
        console.warn('No delivery found when removing item from cart');
        return;
      }

      console.info(`Removing item ${item.id} from delivery`);

      const items = delivery.items || [];
      const itemIds = items.map((item) => item.id);
      const itemIdx = itemIds.indexOf(item.id);
      if (itemIdx < 0) {
        console.error(`Item ${item.id} not found when removing from cart`);
        return;
      }

      const updatedItems = [...items];
      updatedItems.splice(itemIdx, 1);

      const updatedDelivery = { ...delivery };
      updatedDelivery.items = updatedItems;

      if (updatedItems.length === 0) {
        updateDelivery(undefined);
        onDismiss();
      } else {
        updateDelivery(updatedDelivery);
      }
    },
    [delivery, onDismiss, updateDelivery]
  );

  const onTypeChange = useCallback(
    (type: DeliveryType) => {
      if (type === deliveryType) {
        return;
      }

      updateType(type);
      if (delivery && delivery.status === DeliveryStatus.draft && delivery.type !== type) {
        updateDelivery(setType(delivery, type));
      }
    },
    [delivery, deliveryType, updateDelivery, updateType]
  );

  const renderItem = useCallback(
    (item: DeliveryItem, border = true) => {
      return (
        <div key={item.id} className={`mx-4 lg:mx-6 mt-4 pb-4 border-gray-200 ${border ? 'border-b' : ''}`}>
          <div className="flex">
            <div className="flex items-center justify-center bg-gray-100 h-5 w-5">
              <p className="text-xs pb-2px">{item.quantity}</p>
            </div>
            <div className="ml-4 mr-4">
              <p className="text-sm font-medium">{item.name}</p>
              {item.notes && <Detail className="mt-1">{item.notes}</Detail>}
            </div>
            <div
              className="flex items-center justify-center h-5 w-5 ml-auto cursor-pointer"
              onClick={() => onRemove(item)}
            >
              <img src="/images/x-gray.png" alt="remove" className="w-3 h-3 mr-1" />
            </div>
          </div>
        </div>
      );
    },
    [onRemove]
  );

  const renderContent = useCallback(() => {
    if (!delivery) {
      return null;
    }

    const items = delivery.items || [];
    const [part1, part2] = getRecap(delivery.relationshipId, delivery.type);

    return (
      <div className="relative flex flex-col h-full overflow-scroll">
        <div className="mx-4 lg:mx-6 grid grid-cols-3 items-center mt-6">
          <img src="/images/x-black.png" className="w-4 h-4 cursor-pointer" alt="dismiss" onClick={onDismiss} />
          <p className="text-center text-gray-600">Your Order</p>
        </div>

        <div className="mx-auto mt-6 flex items-center">
          <div className="flex items-center">
            <Fulfillment deliveryType={deliveryType} onSetDeliveryType={onTypeChange} />
          </div>
        </div>

        <div className="flex flex-col mx-4 lg:mx-8 mt-8">
          <div className="bg-white">
            <div className="flex flex-col pt-3 lg:pt-5">
              <p className="mx-4 lg:mx-6 text-base font-medium">{getName(delivery.relationshipId)}</p>
              <p className="ml-4 lg:ml-6 mr-12 mt-1 text-sm text-gray-400">{part1}</p>
              {part2 && <p className="ml-4 lg:ml-6 mr-12 mt-0.5 text-sm text-gray-400">{part2}</p>}
              <Divider className="mx-4 lg:mx-6 mt-4" />
            </div>
            {items.map((item) => renderItem(item, true))}
            <div className="flex items-center p-4 lg:px-6 lg:pb-5 cursor-pointer" onClick={onDismiss}>
              <p className={`text-sm text-${Colors.secondaryText}`}>Add another item</p>
              <img src="/images/arrow-right-gray.png" className="ml-auto mr-2 h-3" alt="" />
            </div>
          </div>
          <div className="mt-6 mb-12">
            <Pricing delivery={delivery} />
          </div>
        </div>

        <div className="mx-6 lg:mx-8 mt-auto mb-5 lg:mb-6 w-floating lg:w-floating-big">
          <p className="px-3 mb-4 text-sm text-gray-400 text-center">
            Don&apos;t worry, you won&apos;t be charged until you review the delivery fee and confirm your order.
          </p>

          <MainButton className="w-full" loading={loading} title="Start Checkout" onClick={onNext} />
        </div>
      </div>
    );
  }, [delivery, deliveryType, loading, onDismiss, onNext, onTypeChange, renderItem]);

  return type === 'popup' ? (
    <Popup className="bg-gray-100" onDismiss={onDismiss} size="full" showing={showing}>
      {renderContent()}
    </Popup>
  ) : (
    <Sidebar className="" onDismiss={onDismiss} showing={showing}>
      {renderContent()}
    </Sidebar>
  );
};

export default memo(Cart);
